import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import type { Ref } from 'react'
import { ChatInput, type ChatInputHandle } from './ChatInput'
import { useSubmitGateFeedback, type ChatAgentSelectionState } from '@/hooks'
import { useTenantKey } from '@/hooks/useTenantKey'
import type { ChatCommandDescriptor, WorkflowDefinitionSummary, Skill } from '@/types'
import type { ChatAttachment } from '@/types/chat'

export interface PendingGateState {
  runId: string
  feedbackAvailable: boolean
}

interface ChatComposerProps {
  projectId: string
  chatId: string | null
  pendingGate: PendingGateState | null
  composerRef?: Ref<ChatInputHandle>

  isContainerMode: boolean
  containerLockedByAttachments: boolean
  onContainerModeChange: (value: boolean) => void
  onSendMessage: (message: string, useContainer: boolean) => boolean
  onCancel: () => void
  onCommandSelect: (command: ChatCommandDescriptor) => void
  onWorkflowSelect: (workflow: WorkflowDefinitionSummary) => void
  commands: ChatCommandDescriptor[]
  workflows: WorkflowDefinitionSummary[]
  skills: Skill[]
  skillsLoading?: boolean
  selectedSkills: Skill[]
  onSelectSkill: (skill: Skill) => void
  onRemoveSkill: (skillId: string) => void
  onClearSkills: () => void
  isStreaming: boolean
  isCancelling: boolean
  disabled: boolean
  attachments: ChatAttachment[]
  attachmentPreviews: Record<string, string>
  pendingUploadCount: number
  onAttachFiles: (files: File[], withPreviews?: boolean) => void
  onCancelUpload: () => void
  onRemoveAttachment: (id: string) => void
  agent: ChatAgentSelectionState
}

export function ChatComposer(props: ChatComposerProps) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  const submitFeedback = useSubmitGateFeedback(props.projectId, props.pendingGate?.runId ?? null)

  const gate = props.pendingGate

  const handleSendFeedback = (message: string) => {
    if (props.selectedSkills.length > 0) props.onClearSkills()
    submitFeedback.mutate(message, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: tenantKey('projects', props.projectId, 'chats') })
      },
      onError: () => toast.error('Failed to send feedback to the waiting gate'),
    })
    return true
  }

  return (
    <div>
      <ChatInput
        chatId={props.chatId}
        composerRef={props.composerRef}
        isContainerMode={props.isContainerMode}
        containerLockedByAttachments={props.containerLockedByAttachments}
        onContainerModeChange={props.onContainerModeChange}
        onSendMessage={gate?.feedbackAvailable ? handleSendFeedback : props.onSendMessage}
        onCancel={props.onCancel}
        onCommandSelect={props.onCommandSelect}
        onWorkflowSelect={props.onWorkflowSelect}
        commands={props.commands}
        workflows={props.workflows}
        skills={props.skills}
        skillsLoading={props.skillsLoading}
        selectedSkills={props.selectedSkills}
        onSelectSkill={props.onSelectSkill}
        onRemoveSkill={props.onRemoveSkill}
        isStreaming={props.isStreaming || submitFeedback.isPending}
        isCancelling={props.isCancelling}
        disabled={props.disabled || (!!gate && !gate.feedbackAvailable)}
        attachments={props.attachments}
        attachmentPreviews={props.attachmentPreviews}
        pendingUploadCount={props.pendingUploadCount}
        onAttachFiles={props.onAttachFiles}
        onCancelUpload={props.onCancelUpload}
        onRemoveAttachment={props.onRemoveAttachment}
        agent={props.agent}
      />
    </div>
  )
}
